/* ======================
   Lieferzeitenlogik und Geschenkverpackung
   VERSION: v3.4 - Datumsbasierte Lieferzeit
   
   PDP-Lieferzeit wird im Custom Liquid Block gehandhabt.
   Dieses Script kümmert sich um:
   - Cart Drawer Lieferzeiten
   - Fetch Interceptor für Cart API
   - Geschenkverpackung auf PDP
   - Teillieferung Checkbox
====================== */

(function() {
  'use strict';

  /* ======================
     CONFIG & CONSTANTS
  ====================== */
  const UNBEKANNT_AB_TAGEN = 100;
  const FALLBACK_MIN = 14;
  const FALLBACK_MAX = 21;

  const MONTH_TRANSLATIONS = {
    'January': 'Januar', 'February': 'Februar', 'March': 'März',
    'April': 'April', 'May': 'Mai', 'June': 'Juni',
    'July': 'Juli', 'August': 'August', 'September': 'September',
    'October': 'Oktober', 'November': 'November', 'December': 'Dezember'
  };

  const MONTHS_DE = {
    'Januar': 0, 'Februar': 1, 'März': 2, 'April': 3,
    'Mai': 4, 'Juni': 5, 'Juli': 6, 'August': 7,
    'September': 8, 'Oktober': 9, 'November': 10, 'Dezember': 11
  };

  const SELECTORS = {
    lineItem: '.line-item',
    cartDrawer: '.cart-drawer,[data-cart-drawer],#cart-drawer',
    quantityInput: 'input[name="updates[]"], .quantity-selector__input',
    teillieferungCheckbox: '.cart-teillieferung__checkbox',
    addToCartForm: 'form[action*="/cart/add"]',
    lieferzeitListItem: '.line-item__lieferzeit'
  };

  const API_ENDPOINTS = {
    cart: '/cart.js',
    add: '/cart/add',
    change: '/cart/change.js',
    update: '/cart/update.js'
  };

  /* ======================
     UTILITY FUNCTIONS
  ====================== */
  const Utils = {

    translateMonth(dateStr) {
      let result = dateStr;
      for (const [en, de] of Object.entries(MONTH_TRANSLATIONS)) {
        result = result.replace(en, de);
      }
      return result;
    },

    berechneLieferdaten(minTage, maxTage) {
      if (minTage >= UNBEKANNT_AB_TAGEN || maxTage >= UNBEKANNT_AB_TAGEN) {
        return null;
      }

      const heutesJahr = new Date().getFullYear();
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + minTage);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + maxTage);

      const jahreswechsel = minDate.getFullYear() !== maxDate.getFullYear();
      const naechstesJahr = maxDate.getFullYear() !== heutesJahr;
      const mitJahr = jahreswechsel || naechstesJahr;

      const optsOhneJahr = { day: 'numeric', month: 'short' };
      const optsMitJahr  = { day: 'numeric', month: 'short', year: 'numeric' };

      return {
        minStr: minDate.toLocaleDateString('de-DE', mitJahr ? optsMitJahr : optsOhneJahr),
        maxStr: maxDate.toLocaleDateString('de-DE', mitJahr ? optsMitJahr : optsOhneJahr)
      };
    },

    calcLieferzeit(inventory, quantity, lieferMin, lieferMax, inventoryTracked) {
      if (inventoryTracked === 'false') {
        return 'Gutscheinversand per E-Mail sofort nach Bestellung';
      }
      if (inventory >= quantity) {
        return '1 bis 3 Werktage (DE)';
      }

      const dates = this.berechneLieferdaten(
        parseInt(lieferMin, 10) || FALLBACK_MIN,
        parseInt(lieferMax, 10) || FALLBACK_MAX
      );
      if (!dates) return 'Lieferzeit aktuell nicht bekannt – wir informieren Sie nach dem Kauf';
      return `Lieferung zwischen ${dates.minStr} und ${dates.maxStr}`;
    },

    parseJSON(str, fallback) {
      try { return JSON.parse(str); } catch (e) { return fallback || {}; }
    },

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => { clearTimeout(timeout); func(...args); };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  };

  /* ======================
     CART API
  ====================== */
  const CartAPI = {
    cache: null,
    cacheTime: 0,
    CACHE_DURATION: 1000,

    async getCart(forceRefresh) {
      const now = Date.now();
      if (!forceRefresh && this.cache && (now - this.cacheTime) < this.CACHE_DURATION) {
        return this.cache;
      }
      try {
        const response = await fetch(API_ENDPOINTS.cart);
        this.cache = await response.json();
        this.cacheTime = now;
        return this.cache;
      } catch (err) {
        console.error('[Cart Script] Failed to fetch cart:', err);
        return this.cache || { items: [] };
      }
    },

    invalidateCache() {
      this.cache = null;
      this.cacheTime = 0;
    },

    async updateAttributes(attributes) {
      this.invalidateCache();
      return fetch(API_ENDPOINTS.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
    }
  };

  /* ======================
     FETCH INTERCEPTOR
  ====================== */
  const FetchInterceptor = {
    originalFetch: window.fetch.bind(window),
    isInstalled: false,

    parseRequestBody(body) {
      if (!body) return null;
      if (typeof body === 'string') return Utils.parseJSON(body, null);
      if (body instanceof FormData || body instanceof URLSearchParams) {
        const result = {};
        for (const [key, value] of body.entries()) result[key] = value;
        return result;
      }
      return null;
    },

    extractProperties(body) {
      const props = {};
      if (!body) return props;
      Object.keys(body).forEach(key => {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          const propName = key.slice(11, -1);
          const propValue = body[key];
          if (propValue !== '' && propValue != null) props[propName] = propValue;
        }
      });
      return props;
    },

    async interceptAdd(url, options) {
      const body = this.parseRequestBody(options?.body);
      if (!body?.id) return null;

      const variantId = parseInt(body.id, 10);
      if (isNaN(variantId)) return null;

      const cart = await CartAPI.getCart(true);
      const existingItem = cart.items.find(item => item.variant_id === variantId);
      if (!existingItem) return null;

      const addQuantity = parseInt(body.quantity, 10) || 1;
      const newQuantity = existingItem.quantity + addQuantity;
      const extractedProps = this.extractProperties(body);
      const newProps = { ...(existingItem.properties || {}), ...extractedProps };
      if (extractedProps['Lieferung']) newProps['Lieferung'] = extractedProps['Lieferung'];

      const changeBody = {
        id: existingItem.key,
        quantity: newQuantity,
        properties: newProps
      };
      if (body.sections) changeBody.sections = body.sections;
      if (body.sections_url) changeBody.sections_url = body.sections_url;

      CartAPI.invalidateCache();
      return this.originalFetch(API_ENDPOINTS.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(changeBody)
      });
    },

    async interceptChange(url, options) {
      const body = this.parseRequestBody(options?.body);
      if (!body?.id) return null;

      const variantIdFromKey = body.id.toString().split(':')[0];
      const variantId = parseInt(variantIdFromKey, 10);
      if (isNaN(variantId)) return null;

      const requestedSections = body.sections;
      const requestedSectionsUrl = body.sections_url;

      const cart = await CartAPI.getCart(true);
      const currentItem = cart.items.find(i => i.variant_id === variantId);
      if (!currentItem) return null;

      const newQuantity = body.quantity !== undefined ? body.quantity : currentItem.quantity;
      const lineItemEl = document.querySelector('[data-variant-id="' + variantId + '"]');

      let bodyModified = false;

      if (lineItemEl && newQuantity !== currentItem.quantity) {
        const dataset = lineItemEl.dataset;
        const inventory = parseInt(dataset.inventory || 0, 10);
        const lieferMin = parseInt(dataset.lieferMin || FALLBACK_MIN, 10);
        const lieferMax = parseInt(dataset.lieferMax || FALLBACK_MAX, 10);
        const inventoryTracked = dataset.inventoryTracked || 'true';

        const newLieferzeit = Utils.calcLieferzeit(
          inventory, newQuantity, lieferMin, lieferMax, inventoryTracked
        );

        const updatedProps = { ...(body.properties || currentItem.properties || {}) };
        updatedProps['Lieferung'] = newLieferzeit;
        body.properties = updatedProps;
        bodyModified = true;
      }

      if (currentItem.key !== body.id) {
        body.id = currentItem.key;
        bodyModified = true;
      }

      if (requestedSections) body.sections = requestedSections;
      if (requestedSectionsUrl) body.sections_url = requestedSectionsUrl;

      if (bodyModified) {
        CartAPI.invalidateCache();
        return this.originalFetch(url, {
          ...options,
          body: JSON.stringify(body)
        });
      }

      return null;
    },

    install() {
      if (this.isInstalled) return;
      this.isInstalled = true;

      const self = this;

      window.fetch = function(...args) {
        const [url, options] = args;
        const urlStr = url?.toString() || '';

        try {
          if (urlStr.includes(API_ENDPOINTS.add)) {
            return self.interceptAdd(url, options)
              .then(result => result || self.originalFetch(...args))
              .catch(() => self.originalFetch(...args));
          }
          if (urlStr.includes(API_ENDPOINTS.change)) {
            return self.interceptChange(url, options)
              .then(result => result || self.originalFetch(...args))
              .catch(() => self.originalFetch(...args));
          }
        } catch (err) {
          // Silent fail
        }

        return self.originalFetch(...args);
      };
    }
  };

  /* ======================
     LINE ITEM MANAGER (Cart Drawer)
  ====================== */
  const LineItemManager = {

    getLineItemData(lineItemEl) {
      const dataset = lineItemEl.dataset;
      return {
        variantId: parseInt(dataset.variantId, 10),
        inventory: parseInt(dataset.inventory || 0, 10),
        lieferMin: parseInt(dataset.lieferMin || FALLBACK_MIN, 10),
        lieferMax: parseInt(dataset.lieferMax || FALLBACK_MAX, 10),
        inventoryTracked: dataset.inventoryTracked || 'true'
      };
    },

    getQuantity(lineItemEl) {
      const qtyInput = lineItemEl.querySelector(SELECTORS.quantityInput);
      return qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    },

    ensureLieferzeitElement(lineItemEl) {
      let liEl = lineItemEl.querySelector(SELECTORS.lieferzeitListItem);
      if (liEl) return liEl;

      const list = lineItemEl.querySelector('ul[role="list"]') || lineItemEl.querySelector('ul');
      if (!list) return null;

      liEl = document.createElement('li');
      liEl.className = 'subheading text-subdued line-item__lieferzeit';
      list.appendChild(liEl);
      return liEl;
    },

    setLieferzeitStatus(lieferzeitEl, text) {
      if (!lieferzeitEl) return;

      let displayText = text;
      if (
        text &&
        !text.includes('per E-Mail') &&
        !text.startsWith('Wareneingang') &&
        !text.startsWith('Lieferung') &&
        !text.startsWith('Lieferzeit aktuell')
      ) {
        displayText = 'Lieferzeit ' + text;
      }

      lieferzeitEl.textContent = displayText;

      lieferzeitEl.classList.remove('lieferzeitlagernd', 'lieferzeitnichtlagernd');

      let status = 'bestellt';
      if (text.includes('1 bis 3 Werktage')) {
        status = 'lagernd';
        lieferzeitEl.classList.add('lieferzeitlagernd');
      } else if (text.includes('per E-Mail')) {
        status = 'digital';
      } else if (text.includes('aktuell nicht bekannt')) {
        status = 'unbekannt';
        lieferzeitEl.classList.add('lieferzeitnichtlagernd');
      } else if (text.startsWith('Lieferung zwischen')) {
        status = 'vorbestellt';
        lieferzeitEl.classList.add('lieferzeitnichtlagernd');
      }

      lieferzeitEl.setAttribute('data-status', status);
    },

    updateLieferzeit(lineItemEl) {
      const data = this.getLineItemData(lineItemEl);
      const qty = this.getQuantity(lineItemEl);

      const lieferzeit = Utils.calcLieferzeit(
        data.inventory,
        qty,
        data.lieferMin,
        data.lieferMax,
        data.inventoryTracked
      );

      const lieferzeitEl = this.ensureLieferzeitElement(lineItemEl);
      this.setLieferzeitStatus(lieferzeitEl, lieferzeit);
      return lieferzeit;
    }

  };


  /* ======================
   CART DELIVERY CALCULATOR
   Berechnet die späteste Lieferung aller Cart-Items
   und schreibt sie als Cart Attribute für die Order
====================== */
const CartDeliveryCalculator = {

  async calculateAndUpdate() {
    const cart = await CartAPI.getCart(true);
    if (!cart.items || cart.items.length === 0) {
      return;
    }

    let latestMaxTage = 0;
    let hasUnbekannt = false;

    for (const item of cart.items) {
      const lineItemEl = document.querySelector(
        `[data-variant-id="${item.variant_id}"]`
      );
      if (!lineItemEl) continue;

      const dataset = lineItemEl.dataset;
      const inventory = parseInt(dataset.inventory || 0, 10);
      const inventoryTracked = dataset.inventoryTracked === 'true';
      const maxTage = parseInt(dataset.lieferMax || 0, 10);

      // Digitale Produkte → ignorieren
      if (!inventoryTracked) continue;

      // Lagernd → 3 Tage
      if (inventory >= item.quantity) {
        if (3 > latestMaxTage) latestMaxTage = 3;
        continue;
      }

      // Unbekannt → Flag setzen
      if (maxTage >= UNBEKANNT_AB_TAGEN) {
        hasUnbekannt = true;
        continue;
      }

      // Normaler Fall → MaxTage vergleichen
      if (maxTage > latestMaxTage) latestMaxTage = maxTage;
    }

    // Spätestes Datum ermitteln
    let lieferungBis;
    if (hasUnbekannt) {
      lieferungBis = 'unbekannt';
    } else {
      const maxFromTage = new Date();
      maxFromTage.setDate(maxFromTage.getDate() + latestMaxTage);

      const finalDate = maxFromTage;

      // Format: YYYY-MM-DD (maschinenlesbar für Flow)
      const y = finalDate.getFullYear();
      const m = String(finalDate.getMonth() + 1).padStart(2, '0');
      const d = String(finalDate.getDate()).padStart(2, '0');
      lieferungBis = `${y}-${m}-${d}`;
    }

    try {
      await CartAPI.updateAttributes({
        'spaeteste_lieferung': lieferungBis
      });
    } catch (err) {
      console.error('[Cart Script] Späteste Lieferung update error:', err);
    }
  }
};

  /* ======================
     DRAWER HANDLERS
  ====================== */
  const DrawerHandlers = {
    bound: false,

    async handleTeillieferungChange(checkboxEl) {
      try {
        const value = checkboxEl.checked ? 'Ja' : '';
        await CartAPI.updateAttributes({ 'Teillieferung gewünscht': value });
      } catch (err) {
        console.error('[Cart Script] Teillieferung error:', err);
      }
    },

    bind() {
      if (this.bound) return;
      this.bound = true;

      document.addEventListener('change', (e) => {
        if (e.target.matches(SELECTORS.teillieferungCheckbox)) {
          this.handleTeillieferungChange(e.target);
        }
      }, true);
    }
  };

  /* ======================
     CART DRAWER OBSERVER
  ====================== */
  const DrawerObserver = {
    observer: null,

   processLineItems: Utils.debounce(function(items) {
  items.forEach(li => LineItemManager.updateLieferzeit(li));
  CartDeliveryCalculator.calculateAndUpdate();
}, 100),

    start() {
      const root = document.querySelector(SELECTORS.cartDrawer) || document.body;

      this.observer = new MutationObserver((mutations) => {
        const addedLineItems = [];

        for (const mutation of mutations) {
          if (mutation.type !== 'childList') continue;
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches(SELECTORS.lineItem)) {
              addedLineItems.push(node);
            } else if (node.querySelectorAll) {
              addedLineItems.push(...node.querySelectorAll(SELECTORS.lineItem));
            }
          }
        }

        if (addedLineItems.length > 0) this.processLineItems(addedLineItems);
      });

      this.observer.observe(root, { childList: true, subtree: true });
    }
  };

  /* ======================
     GIFTWRAP (PDP)
  ====================== */
  const Giftwrap = {
    TEMPLATE_ID: 'giftwrap-template',

    createTemplate() {
      if (document.getElementById(this.TEMPLATE_ID)) return;
      const template = document.createElement('template');
      template.id = this.TEMPLATE_ID;
      template.innerHTML = '<div class="ProductMeta__GiftWrap gift-wrap"><label class="gift-wrap__label"><input type="checkbox" class="gift-wrap__checkbox"><span>Kostenlos als Geschenk verpacken</span></label></div>';
      document.body.appendChild(template);
    },

    shouldShowGiftwrap(form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (!variantIdInput?.value) return false;
      const variantDiv = document.querySelector('.variant-delivery-time-' + variantIdInput.value);
      if (!variantDiv) return true;
      const inventoryTracked = variantDiv.dataset.inventoryTracked || 'true';
      const available = variantDiv.dataset.available;
      return inventoryTracked !== 'false' && available === 'true';
    },

    updateVisibility(form) {
      const giftwrapDiv = form.querySelector('.gift-wrap');
      if (!giftwrapDiv) return;
      const label = giftwrapDiv.querySelector('.gift-wrap__label');
      if (!label) return;
      if (this.shouldShowGiftwrap(form)) {
        label.classList.remove('gift-wrap__label--hidden');
      } else {
        label.classList.add('gift-wrap__label--hidden');
        const checkbox = giftwrapDiv.querySelector('.gift-wrap__checkbox');
        if (checkbox) checkbox.checked = false;
      }
    },

    injectIntoForm(form) {
      if (form.querySelector('.gift-wrap')) return;
      if (!this.shouldShowGiftwrap(form)) return;

      const templateEl = document.getElementById(this.TEMPLATE_ID);
      if (!templateEl) return;

      const node = templateEl.content.firstElementChild.cloneNode(true);
      const checkbox = node.querySelector('.gift-wrap__checkbox');

      checkbox.addEventListener('change', function() {
        const existingHidden = form.querySelector('input[name="properties[Geschenkverpackung]"]');
        if (existingHidden) existingHidden.remove();
        if (checkbox.checked) {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'properties[Geschenkverpackung]';
          hidden.value = 'Ja';
          form.appendChild(hidden);
        }
      });

      const submitBtn = form.querySelector('button[name="add"], [type="submit"], .ProductForm__AddToCart');
      const group = submitBtn?.closest('.button-group, .product-form__buttons, .ProductForm__BuyButtons') || submitBtn?.parentElement;

      if (group) {
        group.insertAdjacentElement('beforebegin', node);
      } else {
        form.prepend(node);
      }
    },

    init() {
      this.createTemplate();
      const self = this;

      document.querySelectorAll(SELECTORS.addToCartForm).forEach(form => {
        self.injectIntoForm(form);
      });

      document.addEventListener('variant:change', function() {
        document.querySelectorAll(SELECTORS.addToCartForm).forEach(form => {
          if (!form.querySelector('.gift-wrap')) self.injectIntoForm(form);
          self.updateVisibility(form);
        });
      });

      const productContainer = document.querySelector('.product, .product-page, [data-section-type="product"]') || document.body;
      const formObserver = new MutationObserver(Utils.debounce(function() {
        document.querySelectorAll(SELECTORS.addToCartForm).forEach(form => {
          self.injectIntoForm(form);
        });
      }, 200));

      formObserver.observe(productContainer, { childList: true, subtree: true });
    }
  };

  /* ======================
     INITIALIZATION
  ====================== */
  function init() {
    FetchInterceptor.install();
    Giftwrap.init();
    DrawerHandlers.bind();
    DrawerObserver.start();

    document.querySelectorAll(SELECTORS.lineItem).forEach(li => {
      LineItemManager.updateLieferzeit(li);
    });

    // Späteste Lieferung initial berechnen
    CartDeliveryCalculator.calculateAndUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();